import React, { useCallback, useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import { AuthContext } from "../../../context/AuthContext";
import SelectInput from "../../../components/UI/SelectInput/SelectInput";
import RadioButtonFormik from "../../../components/UI/RadioButton/RadioButtonFormik";
import SelectInputCachedFormik from "../../../components/UI/SelectInput/SelectInputCachedFormik";
import HtmlEditor from "../../../components/UI/HtmlEditorInput/HtmlEditor";
import useRevisao from "./hooks/useRevisao";

const validationSchema = Yup.object().shape({
  sqTributo: Yup.string().default("").required("Selecione o tributo"),
  txTipoOcorrencia: Yup.string().default("").required("Digite a Ocorrência"),
  dsTipoOcorrencia: Yup.string().default("").required("Digite a descrição"),
  txOrientacao: Yup.string().default("").nullable(),
  txPalavraChave: Yup.string().default("").required("Digite a(s) palavra(s)-chave(s)"),
  txInfringencia: Yup.string().default("").required("Digite a infringência"),
  txCompInfringencia: Yup.string().default("").required("Digite o complemento da infringência"),
  txPenalidade: Yup.string().default("").required("Digite a penalidade"),
  txCompPenalidade: Yup.string().default("").required("Digite o complemento da penalidade"),
  fgImposto: Yup.string().default(""),
  fgAtivoConsumo: Yup.string().default(""),
  fgAgregado: Yup.string().default(""),
  fgDireitoCredito: Yup.string().default(""),
  tpSentidoOperacao: Yup.string().default("").required("Selecione o Sentido da Operação"),
  tpMercadoria: Yup.string().default("").required("Selecione o Tipo de Mercadoria"),
  sqTipoDetalhamento: Yup.string().default("").required("Selecione o Tipo de Detalhamento"),
  sqCalculoMulta: Yup.string().default("").required("Selecione o Cáculo da Multa"),
  sqModeloImportacao: Yup.string().default(""),
  sqTipoAutomacao: Yup.string().default(""),
  sqTipoOcorrencia: Yup.string().default(""),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (key === "sqTributo") {
    initialValues[key] = 1;
  } else if (key === "fgAtivoConsumo" || key === "fgAgregado" || key === "fgDireitoCredito" || key === "fgImposto") {
    initialValues[key] = "N";
  } else if (key === "tpSentidoOperacao") {
    initialValues[key] = "A";
  } else if (key === "tpMercadoria") {
    initialValues[key] = "T";
  } else {
    initialValues[key] = "";
  }
});

const FormOcorrencia = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  // Alterar Status Revisão
  const { Revisar } = useRevisao();

  // Formik
  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (!values.sqTipoOcorrencia) submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluirOcorrencia, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.alterarOcorrencia, values);
    },
    [submitRequest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { values, setSubmitting, setValues } = formik;

  useEffect(() => {
    if (submitIsLoading) return;

    if (submitData || submitErrors) setSubmitting(false);

    if (submitData) {
      toast.success("Ocorrência salva com sucesso");
      history.push("/app/ocorrencia");
    }
  }, [setSubmitting, submitData, submitErrors, submitIsLoading, history]);

  // Carregar item
  const {
    request: ocorrenciaRequest,
    data: ocorrenciaData,
    errors: ocorrenciaErrors,
    isLoading: ocorrenciaIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      setSubmitting(true);
      ocorrenciaRequest(apiRoutes.fiscalizacao.parametrizacao.consultarOcorrencia, { Sequencial: state.itemKey });
      delete state.itemKey;
    }
  }, [ocorrenciaRequest, setSubmitting, state]);

  useEffect(() => {
    if (ocorrenciaIsLoading) return;

    setSubmitting(false);

    if (ocorrenciaErrors) {
      toast.error(ocorrenciaErrors.message);
    }

    if (!values.sqTipoOcorrencia && ocorrenciaData && ocorrenciaData.result.length) {
      const info = {
        codigo: ocorrenciaData?.result[0]?.sqTipoOcorrencia,
        data: {
          nomeFormalInclusao: ocorrenciaData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: ocorrenciaData?.result[0]?.nomeFormalAlteracao,
          documentoInclusao: ocorrenciaData?.result[0]?.usuarioInclusao,
          documentoAlteracao: ocorrenciaData?.result[0]?.usuarioAlteracao,
          dataInclusao: ocorrenciaData?.result[0]?.dataInclusao,
          dataAlteracao: ocorrenciaData?.result[0]?.dataAlteracao,
        },
      };

      setValues({ ...ocorrenciaData.result[0] });
      setDadosInfo(info);
    }
  }, [setValues, setSubmitting, ocorrenciaData, ocorrenciaErrors, ocorrenciaIsLoading, values.sqTipoOcorrencia]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Ocorrência</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.codigo ? "Alteração de Ocorrência" : "Nova Ocorrência"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="codigo" />
                </Col>
              </Row>
            </div>

            <div className="card-body">
              <FormAlert formik={formik} data={submitData} errors={submitErrors} isLoading={submitIsLoading} />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sqTipoOcorrencia
                      ? !isAllowed(funcionalidades.tipoOcorrencia.alterar)
                      : !isAllowed(funcionalidades.tipoOcorrencia.inserir)
                  }
                >
                  {!formik.values.sqTipoOcorrencia ? (
                    ""
                  ) : (
                    <Row>
                      <Form.Group as={Col} sm={12} md={3}>
                        <Form.Label>Número</Form.Label>
                        <br />
                        <strong>{formik.values.sqTipoOcorrencia}</strong>
                      </Form.Group>
                    </Row>
                  )}

                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Tributo</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sqTributo" tipoItem="tipotributo" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Texto</Form.Label>
                      <HtmlEditor formik={formik} field="txTipoOcorrencia" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Descrição</Form.Label>
                      <HtmlEditor formik={formik} field="dsTipoOcorrencia" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Orientação (não obrigatório)</Form.Label>
                      <HtmlEditor formik={formik} field="txOrientacao" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Palavra-chave</Form.Label>
                      <TextInputFormik formik={formik} field="txPalavraChave" maxLength="1000" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Infringência</Form.Label>
                      <TextInputFormik formik={formik} field="txInfringencia" maxLength="1000" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Complemento da Infringência</Form.Label>
                      <TextInputFormik formik={formik} field="txCompInfringencia" maxLength="1000" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Penalidade</Form.Label>
                      <TextInputFormik formik={formik} field="txPenalidade" maxLength="1000" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={10}>
                      <Form.Label>Complemento da Penalidade</Form.Label>
                      <TextInputFormik formik={formik} field="txCompPenalidade" maxLength="1000" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={2}>
                      <Form.Label>Imposto</Form.Label>
                      <br />
                      <RadioButtonFormik inline="inline" formik={formik} field="fgImposto" label="Sim" value="S" />
                      <RadioButtonFormik inline="inline" formik={formik} field="fgImposto" label="Não" value="N" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={2}>
                      <Form.Label>Ativo Fixo / Consumo</Form.Label>
                      <br />
                      <RadioButtonFormik inline="inline" formik={formik} field="fgAtivoConsumo" label="Sim" value="S" />
                      <RadioButtonFormik inline="inline" formik={formik} field="fgAtivoConsumo" label="Não" value="N" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={2}>
                      <Form.Label>Permite Agregado</Form.Label>
                      <br />
                      <RadioButtonFormik inline="inline" formik={formik} field="fgAgregado" label="Sim" value="S" />
                      <RadioButtonFormik inline="inline" formik={formik} field="fgAgregado" label="Não" value="N" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Dá Direito a crédito</Form.Label>
                      <br />
                      <RadioButtonFormik
                        inline="inline"
                        formik={formik}
                        field="fgDireitoCredito"
                        label="Sim"
                        value="S"
                      />
                      <RadioButtonFormik
                        inline="inline"
                        formik={formik}
                        field="fgDireitoCredito"
                        label="Não"
                        value="N"
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Sentido Operação</Form.Label>
                      <SelectInput
                        formik={formik}
                        field="tpSentidoOperacao"
                        options={[
                          { key: "0", value: "", text: "-- Selecione --" },
                          { value: "E", text: "ENTRADA" },
                          { value: "S", text: "SAÍDA" },
                          { value: "A", text: "NÃO SE APLICA" },
                        ]}
                      />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Tipo de Mercadoria</Form.Label>
                      <SelectInput
                        formik={formik}
                        field="tpMercadoria"
                        options={[
                          { key: "0", value: "", text: "-- Selecione --" },
                          { value: "T", text: "TRIBUTADA" },
                          { value: "I", text: "INSENTA" },
                          { value: "N", text: "NÃO TRIBUTADA" },
                          { value: "S", text: "SUBSTITUIÇÃO TRIBUTÁRIA" },
                          { value: "A", text: "NÃO SE APLICA" },
                        ]}
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Cálculo da Multa</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sqCalculoMulta" tipoItem="pltobrigacao" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Tipo de Detalhamento</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sqTipoDetalhamento" tipoItem="tipoDetalhamento" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Modelo de Importação</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sqModeloImportacao" tipoItem="modeloimportacao" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Tipo de Divergência</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sqTipoAutomacao" tipoItem="tipodivergencia" />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2" style={{ marginTop: 30 }}>
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Ocorrência..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.codigo
                            ? funcionalidades.tipoOcorrencia.alterar
                            : funcionalidades.tipoOcorrencia.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      >
                        Salvar
                      </ButtonSubmitFormikPermission>
                    )}
                    {"    "}
                    {!formik.isSubmitting && formik.values.sqTipoOcorrencia && (
                      <Button
                        variant="danger"
                        className="mr-3"
                        onClick={() => {
                          Revisar(formik.values.sqTipoOcorrencia, "D");
                          history.push("/app/ocorrencia");
                        }}
                      >
                        Disponibilizar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && formik.values.sqTipoOcorrencia && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="light" onClick={() => history.goBack()}>
                        Voltar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormOcorrencia;
